import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Upload,
  Trash2,
  Loader2,
  ImagePlus,
  ClipboardList,
  Images,
  Phone,
  MapPin,
  CheckCheck,
  Undo2,
  Car,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "paliway_admin_token";

const inputCls =
  "w-full bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-zinc-500 outline-none transition-colors duration-300 focus:border-emergency";

const Row = ({ icon: Icon, label, value }) =>
  value ? (
    <p className="flex items-start gap-2 text-sm text-zinc-300">
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-dispatch" />
      <span>
        <span className="font-mono text-[10px] tracking-wider uppercase text-zinc-500">{label}: </span>
        {value}
      </span>
    </p>
  ) : null;

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [tab, setTab] = useState("quotes");
  const [items, setItems] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const handle401 = (err) => {
    if (err.response?.status === 401) {
      toast.error("Session expired — log in again.");
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      return true;
    }
    return false;
  };

  const loadGallery = useCallback(() => {
    axios.get(`${API}/gallery`).then((res) => setItems(res.data)).catch(() => {});
  }, []);

  const loadQuotes = useCallback(() => {
    axios
      .get(`${API}/quotes`, authHeaders)
      .then((res) => setQuotes(res.data))
      .catch((err) => handle401(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) {
      loadGallery();
      loadQuotes();
    }
  }, [token, loadGallery, loadQuotes]);

  const login = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome back, boss.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Pick a photo first.");
      return;
    }
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("caption", caption);
      await axios.post(`${API}/gallery`, body, authHeaders);
      toast.success("Photo added to the gallery.");
      setCaption("");
      setFile(null);
      e.target.reset();
      loadGallery();
    } catch (err) {
      if (!handle401(err)) toast.error("Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`, authHeaders);
      toast.success("Photo removed.");
      loadGallery();
    } catch (err) {
      if (!handle401(err)) toast.error("Could not remove photo.");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}`, { status }, authHeaders);
      toast.success(status === "handled" ? "Marked as handled." : "Moved back to new.");
      loadQuotes();
    } catch (err) {
      if (!handle401(err)) toast.error("Could not update request.");
    }
  };

  const newCount = quotes.filter((q) => q.status === "new").length;

  if (!token) {
    return (
      <section className="min-h-[80vh] bg-asphalt py-20 grain relative" data-testid="admin-page">
        <div className="mx-auto max-w-md px-4 sm:px-6">
          <div className="border border-white/10 bg-charcoal p-8">
            <div className="flex items-center gap-3">
              <Lock className="h-6 w-6 text-emergency" />
              <h1 className="font-display text-2xl font-black uppercase text-white">Owner Login</h1>
            </div>
            <p className="mt-2 text-sm text-zinc-400">Paliway Towing dispatch manager.</p>
            <form onSubmit={login} className="mt-6 space-y-4" data-testid="admin-login-form">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} data-testid="admin-email-input" />
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} data-testid="admin-password-input" />
              <button
                type="submit"
                disabled={loginLoading}
                data-testid="admin-login-button"
                className="flex w-full items-center justify-center gap-2 bg-emergency py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-emergency-dark disabled:opacity-60"
              >
                {loginLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                {loginLoading ? "Signing in..." : "Sign In"}
              </button>
            </form>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-[80vh] bg-asphalt py-20 grain relative" data-testid="admin-page">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-emergency uppercase">Dispatch Manager</p>
            <h1 className="mt-2 font-display text-3xl font-black uppercase text-white">Paliway HQ</h1>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-zinc-300 transition-colors hover:border-emergency hover:text-emergency"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>

        <div className="mt-8 grid grid-cols-2 border border-white/10">
          <button
            onClick={() => setTab("quotes")}
            data-testid="admin-tab-quotes"
            className={`flex items-center justify-center gap-2 py-4 font-display text-sm font-bold uppercase tracking-wider transition-colors ${
              tab === "quotes" ? "bg-emergency text-white" : "bg-charcoal text-zinc-400 hover:text-white"
            }`}
          >
            <ClipboardList className="h-4 w-4" /> Quote Requests
            {newCount > 0 && (
              <span className="bg-white text-emergency px-2 py-0.5 font-mono text-[10px] font-bold" data-testid="admin-quotes-new-count">
                {newCount} NEW
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("photos")}
            data-testid="admin-tab-photos"
            className={`flex items-center justify-center gap-2 py-4 font-display text-sm font-bold uppercase tracking-wider transition-colors ${
              tab === "photos" ? "bg-emergency text-white" : "bg-charcoal text-zinc-400 hover:text-white"
            }`}
          >
            <Images className="h-4 w-4" /> Gallery Photos
          </button>
        </div>

        {tab === "quotes" && (
          <div className="mt-8 space-y-4" data-testid="admin-quotes-list">
            {quotes.length === 0 && (
              <p className="border border-white/10 bg-charcoal p-8 text-center font-mono text-xs tracking-widest uppercase text-zinc-500" data-testid="admin-quotes-empty">
                No quote requests yet.
              </p>
            )}
            {quotes.map((q) => {
              const vehicle = [q.vehicle_year, q.vehicle_make, q.vehicle_model].filter(Boolean).join(" ") || q.vehicle;
              return (
                <article
                  key={q.id}
                  data-testid={`admin-quote-${q.id}`}
                  className={`border bg-charcoal p-5 sm:p-6 transition-colors ${
                    q.status === "new" ? "border-emergency/60" : "border-white/10 opacity-75"
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-display text-xl font-bold uppercase text-white">{q.name}</h3>
                        <span
                          className={`px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${
                            q.status === "new" ? "bg-emergency text-white" : "bg-dispatch/20 text-dispatch"
                          }`}
                          data-testid={`admin-quote-status-${q.id}`}
                        >
                          {q.status === "new" ? "New" : "Handled"}
                        </span>
                      </div>
                      <p className="mt-1 font-mono text-[10px] tracking-wider uppercase text-zinc-500">
                        {q.created_at ? new Date(q.created_at).toLocaleString() : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={`tel:${q.phone}`}
                        data-testid={`admin-quote-call-${q.id}`}
                        className="inline-flex items-center gap-2 bg-emergency px-4 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-emergency-dark"
                      >
                        <Phone className="h-3.5 w-3.5" /> {q.phone}
                      </a>
                      <button
                        onClick={() => setStatus(q.id, q.status === "new" ? "handled" : "new")}
                        data-testid={`admin-quote-toggle-${q.id}`}
                        className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-zinc-300 transition-colors hover:border-dispatch hover:text-dispatch"
                      >
                        {q.status === "new" ? <CheckCheck className="h-3.5 w-3.5" /> : <Undo2 className="h-3.5 w-3.5" />}
                        {q.status === "new" ? "Mark Handled" : "Reopen"}
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-2 sm:grid-cols-2">
                    <Row icon={MapPin} label="Pick-up" value={q.pickup_address || q.location} />
                    <Row icon={MapPin} label="Drop-off" value={q.dropoff_address} />
                    <Row icon={Car} label="Vehicle" value={vehicle} />
                    <Row icon={ClipboardList} label="Service" value={q.service} />
                  </div>
                  {(q.reason || q.details) && (
                    <p className="mt-3 border-l-2 border-emergency pl-3 text-sm text-zinc-400">{q.reason || q.details}</p>
                  )}
                </article>
              );
            })}
          </div>
        )}

        {tab === "photos" && (
          <>
            <form onSubmit={upload} className="mt-8 border border-white/10 bg-charcoal p-6 space-y-4" data-testid="admin-upload-form">
              <p className="font-mono text-xs tracking-[0.25em] text-dispatch uppercase">Add a new photo</p>
              <label className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-white/25 px-4 py-8 text-sm text-zinc-400 transition-colors hover:border-emergency hover:text-white" data-testid="admin-file-dropzone">
                <ImagePlus className="h-5 w-5" />
                {file ? file.name : "Tap to choose a job photo (jpg, png, webp — max 15MB)"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="admin-file-input" />
              </label>
              <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (e.g. Camaro winched up the ramp - Fort Worth)" className={inputCls} data-testid="admin-caption-input" />
              <button
                type="submit"
                disabled={uploading}
                data-testid="admin-upload-button"
                className="flex w-full items-center justify-center gap-2 bg-dispatch py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-dispatch-dark disabled:opacity-60"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Uploading..." : "Upload to Gallery"}
              </button>
            </form>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="admin-gallery-list">
              {items.map((item) => (
                <div key={item.id} className="group relative border border-white/10 bg-charcoal">
                  <img src={item.src} alt={item.caption || "Gallery photo"} className="aspect-square w-full object-cover" loading="lazy" />
                  {item.caption && (
                    <p className="truncate px-2.5 py-2 font-mono text-[10px] tracking-wider uppercase text-zinc-400">{item.caption}</p>
                  )}
                  <button
                    onClick={() => remove(item.id)}
                    data-testid={`admin-photo-delete-${item.id}`}
                    className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center bg-emergency text-white opacity-90 transition-opacity hover:opacity-100"
                    aria-label="Delete photo"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Admin;
